import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutlet } from "react-router";
import { useApp } from "../context";
import { AudioDonation, AudioDonationAttributes } from "../parse";
import { AdminAudioListItem } from "./AdminAudioListItem";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(2),
  },
  headerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    float: "right",
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  listItem: {
    paddingRight: 100,
  },
  statusChip: {
    marginRight: theme.spacing(1),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

type FilterType = "all" | "not-in-dataset" | "not-in-game" | "unused";

export const AdminAudioDonationRoute = observer(
  function AdminAudioDonationRoute() {
    const app = useApp();
    const classes = useStyles();
    const navigate = useNavigate();
    const outlet = useOutlet();
    const { t } = useTranslation();
    const [filter, setFilter] = useState<FilterType>("all");
    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
      null,
    );

    const filteredDonations = app.admin.audioDonations.filter(
      (donation: AudioDonationAttributes) => {
        switch (filter) {
          case "not-in-dataset":
            return !donation.dataset;
          case "not-in-game":
            return !donation.game;
          case "unused":
            return !donation.dataset && !donation.game;
          case "all":
          default:
            return true;
        }
      },
    );

    const handleAddToDataset = async (id: string) => {
      await app.admin.addToDataset(id);
    };

    const handleAddToGame = async (id: string) => {
      await app.admin.addToGame(id);
    };

    const handleDelete = (id: string) => {
      app.admin.delete(AudioDonation.className, id);
    };

    const handlePlayAudio = (audioUrl: string, donationId: string) => {
      // If already playing this audio, stop it
      if (playingAudio === donationId) {
        audioElement?.pause();
        setPlayingAudio(null);
        return;
      }

      // Stop any currently playing audio
      if (audioElement) {
        audioElement.pause();
      }

      // Create and play new audio
      const audio = new Audio(audioUrl);
      audio.onended = () => setPlayingAudio(null);
      audio.play();
      setAudioElement(audio);
      setPlayingAudio(donationId);
    };

    if (outlet) {
      return outlet;
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" component="h2" className={classes.padding}>
            {t("admin-audio-donations-management")}
          </Typography>
        </Grid>

        <Grid item xs={12} className={classes.padding}>
          <Box className={classes.filterContainer}>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 200 }}
            >
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                label={t("filter")}
              >
                <MenuItem value="all">{t("all")}</MenuItem>
                <MenuItem value="not-in-dataset">
                  {t("not-in-dataset")}
                </MenuItem>
                <MenuItem value="not-in-game">{t("not-in-game")}</MenuItem>
                <MenuItem value="unused">{t("unused")}</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary">
              {filteredDonations.length} {t("entries")}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <List>
            {filteredDonations.map((donation) => {
              const secondaryText = [
                `${t("language")}: ${donation.language}`,
                `${t("speaker-age")}: ${donation.speakerAge}`,
                `${t("voice-gender")}: ${donation.voiceGender}`,
                `${t("created")}: ${donation.createdAt.toLocaleDateString()}`,
                `${t("text")}: ${
                  donation.spokenText || t("no-text-available")
                }`,
              ].join(" | ");

              return (
                <>
                  <Divider variant="middle" component="li" />
                  <AdminAudioListItem
                    donation={donation}
                    secondaryText={secondaryText}
                    buttons={
                      <>
                        {!donation.dataset && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => handleAddToDataset(donation.id)}
                          >
                            {t("add-to-dataset")}
                          </Button>
                        )}
                        {!donation.game && donation.audio && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => handleAddToGame(donation.id)}
                          >
                            {t("add-to-game")}
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          color="default"
                          onClick={() => handleDelete(donation.id)}
                        >
                          {t("button-delete")}
                        </Button>
                      </>
                    }
                  />
                </>
              );
            })}
          </List>
        </Grid>
      </Grid>
    );
  },
);
